import "./btn.css"

import Icon from "@utilities/Icon"
import classNames from "classnames"
import React, { forwardRef } from "react"

import { BaseButton, BaseButtonElementProps } from "./BaseButton"

export type DropdownButtonProps = {
  /**
   * ID of the element controlled by this dropdown button. Optional when wrapped
   * in `<DropdownTrigger>` (RA's `<Pressable>` injects it from
   * `PressResponderContext`); required when used standalone to drive a custom popover.
   */
  "aria-controls"?: string
  /**
   * Whether the dropdown is expanded or not. Optional when wrapped in
   * `<DropdownTrigger>`; required when used standalone.
   */
  "aria-expanded"?: boolean
  suffix?: never
}

export function dropdownProps({ className }: { className?: string }) {
  return {
    className: classNames(className, "tds-btn--dropdown"),
    suffix: <Icon symbol="general#down-caret" aria-hidden />,
  }
}

/**
 * A button that toggles a dropdown menu or popover. Appends a caret suffix
 * and wires up `aria-controls` and `aria-expanded` to the controlled element.
 *
 * @component
 * @see {@link https://planningcenter.github.io/tapestry/?path=/docs/components-button-dropdown-button--docs | Storybook Documentation}
 */
export const DropdownButton = forwardRef<
  HTMLButtonElement,
  BaseButtonElementProps & DropdownButtonProps
>((props, ref) => {
  return <BaseButton ref={ref} {...props} {...dropdownProps(props)} />
})

DropdownButton.displayName = "DropdownButton"
